import {useLayoutEffect, useRef} from "react";

import {
    prefixVariable,
    selectionVariable,
    suffixVariable,
} from "@khan/guide-coeditor-core";
import {
    abortAll,
    AudioCache,
    clearPromptVariables,
    getGuideActivitySlug,
    isActivitiesPage,
    isUserDefaultActivityPage,
} from "@khan/guide-core-helpers";
import {GuideStateApi} from "@khan/guide-state";
import {AIActivity} from "@khan/guide-types";
import {isGuideChatDisabled} from "@khan/guide-utils";
import {useUserAttributes} from "@khan/shared-core";
import {useLocation} from "@khan/urls";
import {randomUUID} from "@khan/util-random-uuid";

import {useGuideAudioController} from "../hooks/use-guide-audio-controller.ts";
import {useResetConversation} from "../hooks/use-reset-conversation.ts";
import {useResumeConversation} from "../hooks/use-resume-conversation.ts";
import isIntermediatePath from "../utils/is-intermediate-path.ts";

import {getDefaultPersonaForUser} from "./get-default-persona-for-profile.ts";
import {publishAiGuideNavigationEvent} from "./publish-ai-guide-navigation-event.ts";
import {
    getParentToolFromPath,
    getTeacherToolFromPath,
    isParentToolPage,
    isTeacherToolPage,
} from "./tool-paths.ts";

export const useNavigationEffect = (): void => {
    const {pathname, search} = useLocation();
    const userAttributes = useUserAttributes();
    const resetConversation = useResetConversation();
    const resumeConversation = useResumeConversation();
    const userAsStudent = GuideStateApi.latest().userAsStudent;
    const lastPageOpenedRef = useRef<string | null>(null);
    const chatDisabled = isGuideChatDisabled(pathname);
    const {
        setAudioCache,
        stop: stopAudio,
        setIsVoiceOn,
    } = useGuideAudioController();

    // Handle page navigation
    // ---
    // We reset the bot state whenever we navigate to a new URL as long as the
    // guide acknowledgement has been accepted. Resetting will re-initialize the
    // bot as well if the bot is opened and not disabled.
    //
    // useLayoutEffect is synchronous and allows us to ensure that this effect
    // is fired before the GuideContextProvider's child useEffect's. Since we
    // reset on navigation, we want that to always happen before any other state
    // variables are updated.
    //
    // BEWARE: useLayoutEffect's in child components will run before this
    // effect. This is because the child components are rendered before this
    // component is rendered and useLayoutEffects are synchronous after render.
    useLayoutEffect(() => {
        // We don't want to do any of this work if:
        // 1. the user isn't enrolled in the guide experience,
        // 2. the page hasn't changed, or
        // 3. the page is an intermediate path (see `isIntermediatePath`)
        if (
            !userAttributes?.isAIGuideEnabled ||
            lastPageOpenedRef.current === pathname ||
            isIntermediatePath(pathname)
        ) {
            return;
        }

        lastPageOpenedRef.current = pathname;

        // Abort any pending requests. This is necessary so that we don't
        // continue processing requests that come in after we've already
        // reset.
        abortAll();

        GuideStateApi.set("contentURLPath", null);
        GuideStateApi.set("contextualLinks", []);
        GuideStateApi.set("dynamicBot", null);
        GuideStateApi.set("modelOverride", null);
        GuideStateApi.set("temperatureOverride", null);
        GuideStateApi.set("userHasToggledPersona", false);
        GuideStateApi.set("retryTimeoutSeconds", 0);
        GuideStateApi.set("temperatureOverride", null);

        // Stop any guide audio playback that is currently playing.
        stopAudio();

        // Release cached audio from memory when navigating to a new page.
        // We currently don't have a need for the audio cache to persist
        // across conversations.
        setAudioCache(new AudioCache());

        const activitySlug = getGuideActivitySlug(pathname);
        const aiTool =
            getTeacherToolFromPath(pathname) ?? getParentToolFromPath(pathname);

        // If we are navigating to the chat with historical figure activity,
        // we want to disable voice.
        if (activitySlug === AIActivity.ChatWithHistoricalFigure) {
            setIsVoiceOn(false);
        }
        // Override bot name iff:
        // 1. The user is emulating a student
        // 2. The user is on the default activity page for their persona
        const defaultPersona = getDefaultPersonaForUser(userAttributes);
        if (
            userAsStudent &&
            isUserDefaultActivityPage(pathname, defaultPersona)
        ) {
            GuideStateApi.set("selectedBotName", AIActivity.DefaultLearner);
        } else {
            GuideStateApi.set(
                "selectedBotName",
                // NOTE: Currently the backend routes the proper bot config
                // for content pages excluding Khanmigo Activities. For now,
                // we set the bot config for Khanmigo Activities on navigation.
                // This can be removed once the backend can do this for us.
                // TODO(GL-918): Set this to null on navigation once the backend
                // supports routing bot configs for Khanmigo Activities.
                activitySlug ?? aiTool ?? null,
            );
        }

        clearPromptVariables({
            exclude: [
                "ReadingLevel",
                "GuideLanguage",
                prefixVariable,
                selectionVariable,
                suffixVariable,
            ],
        });

        const navigatedToActivityPage = isActivitiesPage(pathname);
        // If we navigated to a page where the Khanmigo sidebar shouldn't appear,
        // set this state field back to false (the default).
        if (navigatedToActivityPage || chatDisabled) {
            GuideStateApi.set("open", false);
        }

        // We should always send an initial_load event if we are navigating to
        // an activity. The only exceptions are the Academic Essay Feedback and
        // Writing Coach activities, which do not have an initial chat
        // conversation.
        const shouldInitializeForActivityPage =
            navigatedToActivityPage &&
            GuideStateApi.latest().selectedBotName !==
                "activity-academic-essay-feedback" &&
            GuideStateApi.latest().selectedBotName !== "activity-writing-coach";

        // We don't need to send an initial_load event to the server if the
        // chat is disabled on this page or minimized. This will prevent the
        // server from sending the initial message.
        const shouldInitializeForContentPage =
            !chatDisabled && GuideStateApi.latest().open;

        const resumeThreadId = new URLSearchParams(search).get(
            "resumeThreadId",
        );

        // We always want to send an initial_load event when navigating to
        // a teacher tool page.
        const shouldInitializeForAIToolPage =
            isTeacherToolPage(pathname) || isParentToolPage(pathname);

        // We always want to send an initial_load event when navigating to the
        // my-essays page in Writing Coach.
        const shouldInitializeForSiloedWritingCoach =
            pathname === "/writing-coach/essays";

        const initializeAfterReset =
            // If there is a resumeThreadId, we should not initialize after reset
            // as resumeConversation will handle setting up the chat.
            !resumeThreadId &&
            (shouldInitializeForActivityPage ||
                shouldInitializeForContentPage ||
                shouldInitializeForAIToolPage ||
                shouldInitializeForSiloedWritingCoach);

        resetConversation(initializeAfterReset).finally(async () => {
            // If there is a resumeThreadId, open the guide and resume the
            // conversation.
            if (resumeThreadId) {
                // Currently we wait for the conversation to be resumed before
                // opening the sidebar. This is to prevent reinitialization
                // from happening unecessarily (we automatically initialize
                // a conversation when sidebar is open).
                // NOTE(tony): If we want to open the sidebar immediately,
                // consider setting the initializationPromise within
                // resumeConversation.
                await resumeConversation(resumeThreadId);
                GuideStateApi.set("open", true);
            }
        });

        // Generate a unique navigation ID that will persist while the user
        // is on this page and be attached to any AI Guide UI events we send
        // during that time.
        const newNavigationId = randomUUID();
        publishAiGuideNavigationEvent({pathname, newNavigationId});
        GuideStateApi.set("navigation", {
            id: newNavigationId,
            path: pathname,
        });
    }, [
        chatDisabled,
        pathname,
        userAttributes,
        resetConversation,
        resumeConversation,
        search,
        setAudioCache,
        setIsVoiceOn,
        stopAudio,
        userAsStudent,
    ]);
};
